import React, { useState, useEffect, useMemo } from "react";
import { COUNTRY_CODE_OPTIONS, filterCountryCodeOptions } from '../../common/country-codes';
import { parsePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input';
import { Autocomplete} from '@mui/material';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid,
  Paper,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Radio,
  RadioGroup,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import {
  Send as SendIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Phone as PhoneIcon,
  CloudUpload as CloudUploadIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  FlashOn as FlashOnIcon,
  CalendarToday as CalendarTodayIcon,
  Search as SearchIcon,
  Message as MessageIcon,
  Image as ImageIcon,
  UploadFile as UploadFileIcon,

} from "@mui/icons-material";

import { styled } from "@mui/material/styles";
import { RCS_BROADCAST_TEMPLATE, AUDIENCE_SEGMENT_LIST_ENDPOINT, GET_RCS_TEMPLATES } from "../../config/urls";
import axios from "axios";
import { useAuth } from "../../hooks/AuthProvider";

// ── Shared / Common Imports ───────────────────────────────────────────────────
import BackButton from "../../common/back-button";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import RCSTemplatePreview from "../../common/RCSTemplatePreview";
import { EMPTY_CSV_ERROR, csvHasDataRows } from "../BroadcastTemplates/CampaignOptions/UploadCSV";
import BroadcastSections, { AudienceTypePicker, EMPTY_FALLBACK, fallbackError, fallbackFields } from "../CampaignsV2/BroadcastSections";
import { format } from "date-fns";
import typography from "../../cssUtils/typography";
import AppDropdown from "../../common/app-dropdown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ColorsConstants, borderColors } from "../../cssUtils/constants";

// Display names of RCS template types (same as the backend's RCSTemplate.TEMPLATE_TYPE_CHOICES)
const RCS_TEMPLATE_TYPE_LABELS = {
  text_message: "Text Message",
  text_message_with_pdf: "Text Message with PDF",
  rich_card: "Rich Card Standalone",
  carousel: "Rich Card Carousel",
  text_message_with_media: "Media Template",
};

// ── Styled Components ─────────────────────────────────────────────────────────
const UploadArea = styled("div")(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: "8px",
  padding: theme.spacing(4),
  textAlign: "center",
  cursor: "pointer",
  transition: "border-color 0.2s, background-color 0.2s",
  backgroundColor: theme.palette.background.default,
  "&:hover": {
    borderColor: ColorsConstants.primaryBlue100,
    backgroundColor: `${ColorsConstants.primaryBlue100}08`,
  },
}));

const ResultCard = styled(Card)(({ theme, success }) => ({
  borderLeft: `4px solid ${success ? theme.palette.success.main : theme.palette.error.main}`,
  backgroundColor: success
    ? `${theme.palette.success.main}10`
    : `${theme.palette.error.main}10`,
}));

const cardStyle = {
  p: 2.5,
  borderRadius: "8px",
  cursor: "pointer",
  backgroundColor: "#F6F6F6",
  transition: "all 0.2s ease",
  boxShadow: "none",
  border: "1px solid #E5E7EB",
  "&:hover": {
    borderColor: ColorsConstants.primaryBlue100,
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
};

const selectedCardStyle = {
  ...cardStyle,
  borderColor: ColorsConstants.primaryBlue100,
  backgroundColor: "#F0F7FF",
  borderWidth: "2px",
};

// ── Miniature Grid Preview Component ─────────────────────────────────────────
const RCSTemplateGridCard = ({ template, isSelected, onClick }) => {
  let suggestions = [];
  try { suggestions = typeof template.suggestions === 'string' ? JSON.parse(template.suggestions) : (template.suggestions || []); } catch(e) {}
  
  let carouselList = [];
  try { carouselList = typeof template.carousel_list === 'string' ? JSON.parse(template.carousel_list) : (template.carousel_list || []); } catch(e) {}

  const mediaUrl = template.media_file || template.document_url;

  return (
    <Card 
      onClick={onClick}
      sx={{ 
        borderRadius: "12px", 
        cursor: "pointer", 
        border: `2px solid ${isSelected ? ColorsConstants.primaryBlue100 : "#E5E7EB"}`,
        backgroundColor: isSelected ? "#F0F7FF" : "#fff",
        display: "flex",
        flexDirection: "column",
        height: 320,
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: ColorsConstants.primaryBlue100,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
        }
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ p: 2, pb: 1.5, display: "flex", justifyContent: "space-between", alignItems: "flex-start", bgcolor: isSelected ? "#F0F7FF" : "#fff" }}>
         <Box>
            <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "14px", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden", pr: 1 }}>
              {template.name}
            </Typography>
            <Chip label={template.template_use_case} size="small" sx={{ mt: 1, height: 20, fontSize: "10px", fontWeight: 600, color: "#4B5563", bgcolor: "#E5E7EB" }} />
         </Box>
         <Radio checked={isSelected} sx={{ p: 0, color: "#D1D5DB", "&.Mui-checked": { color: ColorsConstants.primaryBlue100 } }} />
      </Box>
      
      {/* ── Canvas (Mimics phone background) ── */}
      <Box sx={{ flex: 1, backgroundColor: "#f4f4f6", p: 1.5, overflow: "hidden", position: "relative", borderTop: "1px solid #E5E7EB" }}>
         
         {/* Image/Video at top */}
         {(template.template_type === "text_message_with_media" || template.template_type === "text_message_with_pdf") && template.message_order !== "text_message_at_top" && mediaUrl && (
            <Box sx={{ bgcolor: "#fff", borderRadius: "12px", overflow: "hidden", mb: 1, height: 100, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
               <Box component="img" src={mediaUrl} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </Box>
         )}

         {/* Text Bubble */}
         {template.text_message_content && (
           <Box sx={{ bgcolor: "#fff", borderRadius: "4px 16px 16px 16px", p: 1.5, mb: 1, boxShadow: "0 1px 2px rgba(0,0,0,0.05)", maxWidth: "95%" }}>
             <Typography sx={{ fontSize: "11px", color: "#1a1a1a", display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.5 }}>
               {template.text_message_content}
             </Typography>
           </Box>
         )}

         {/* Image/Video at bottom */}
         {(template.template_type === "text_message_with_media" || template.template_type === "text_message_with_pdf") && template.message_order === "text_message_at_top" && mediaUrl && (
            <Box sx={{ bgcolor: "#fff", borderRadius: "12px", overflow: "hidden", mb: 1, height: 100, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
               <Box component="img" src={mediaUrl} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </Box>
         )}

         {/* Rich Card */}
         {template.template_type === "rich_card" && (
            <Box sx={{ bgcolor: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", mb: 1 }}>
               <Box sx={{ height: 100, bgcolor: "#e0e0e0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {mediaUrl ? <Box component="img" src={mediaUrl} sx={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <ImageIcon sx={{ color: "#bbb" }} />}
               </Box>
               <Box sx={{ p: 1.5 }}>
                 <Typography sx={{ fontSize: "12px", fontWeight: 700, mb: 0.5 }} noWrap>{template.card_title}</Typography>
                 <Typography sx={{ fontSize: "10px", color: "#666", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4 }}>{template.card_description}</Typography>
               </Box>
            </Box>
         )}

         {/* Carousel */}
         {template.template_type === "carousel" && carouselList.length > 0 && (
            <Box sx={{ display: "flex", gap: 1 }}>
               <Box sx={{ width: "85%", bgcolor: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", flexShrink: 0 }}>
                   <Box sx={{ height: 90, bgcolor: "#e0e0e0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                       {carouselList[0].media_url ? <Box component="img" src={carouselList[0].media_url} sx={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <ImageIcon sx={{ color: "#bbb" }} />}
                   </Box>
                   <Box sx={{ p: 1.5 }}>
                     <Typography sx={{ fontSize: "12px", fontWeight: 700, mb: 0.5 }} noWrap>{carouselList[0].title}</Typography>
                     <Typography sx={{ fontSize: "10px", color: "#666", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4 }}>{carouselList[0].description}</Typography>
                   </Box>
               </Box>
               {carouselList.length > 1 && (
                 <Box sx={{ width: "10%", bgcolor: "#fff", borderRadius: "12px 0 0 12px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", opacity: 0.7 }} />
               )}
            </Box>
         )}

         {/* Suggestions */}
         {suggestions.length > 0 && (
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
              {suggestions.slice(0, 3).map((s, i) => (
                <Chip key={i} label={s.text || s.displayText || `Action ${i+1}`} size="small" variant="outlined" sx={{ fontSize: "9px", height: 22, borderColor: "#1976d2", color: "#1976d2", bgcolor: "#fff", fontWeight: 600 }} />
              ))}
              {suggestions.length > 3 && <Chip label={`+${suggestions.length - 3}`} size="small" variant="outlined" sx={{ fontSize: "9px", height: 22, borderColor: "#1976d2", color: "#1976d2", bgcolor: "#fff", fontWeight: 600 }} />}
            </Box>
         )}

         {/* Gradient overlay to indicate cutoff if content is too long */}
         <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 40, background: "linear-gradient(transparent, #f4f4f6)" }} />
      </Box>
    </Card>
  );
};

const countryOptions = COUNTRY_CODE_OPTIONS.map((option) => ({
  ...option,
  code: option.iso,
  label: option.name,
  dialCode: `+${option.callingCode}`,
}));

// ── Main Component ────────────────────────────────────────────────────────────
const BroadcastTemplate = ({
  bot,
  onBack,
  onSuccess,
  onError,
  props,
  singlePage = null, // Campaigns (Beta): render as one page inside the setup page
}) => {
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(false); // stays true after success so the button can't resubmit before we navigate away
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [successAlert, setSuccessAlert] = useState("");
  const [errors, setErrors] = useState({});
  const { getValidToken } = useAuth();

  // Campaign/broadcast values: from the Campaigns (Beta) page when single-page, else from pageParams
  const campaignsV2Params = singlePage || props?.pageParams || {};
  const campaignName = campaignsV2Params.campaignName || "";
  // Opened from Campaigns (Beta): leave back to it instead of the bot list / Broadcast page
  const fromCampaignsV2 = !!singlePage || props?.pageParams?.lastPage === "campaigns_v2";
  const exitTo = fromCampaignsV2 ? () => props.onNavigate("campaigns_v2", { tab: campaignsV2Params.returnTab }) : onBack;
  // Campaigns (Beta): fallback to WhatsApp for failed recipients (not for quick / single sends)
  const [fallback, setFallback] = useState(EMPTY_FALLBACK);
  const fallbackAvailable = !!singlePage && !singlePage.quick;
  const campaignsV2Fields = {
    existing_campaign_id: campaignsV2Params.existingCampaignId,
    broadcast_name: campaignsV2Params.broadcastName,
    broadcast_description: campaignsV2Params.broadcastDescription,
    ...(fallbackAvailable ? fallbackFields(fallback, "rcs") : {}),
  };
  const campaignDescription = campaignsV2Params.campaignDescription || "";
  const initialCampaignType = campaignsV2Params.campaignType || "standard";

  // ── Stepper State ──
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Select Template", "Select Campaign Type", "Select Broadcast Type"];

  // ── Step 1 State (Templates) ──
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(props?.pageParams?.template || null);
  const [templateSearch, setTemplateSearch] = useState("");

  // ── Step 2 State (Recipients) ──
  const [recipientType, setRecipientType] = useState(initialCampaignType === "quick" ? "single" : "csv");
  const [segments, setSegments] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [recipientInput, setRecipientInput] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState(countryOptions.find(c => c.code === 'IN')); // Default to India
  const [phoneNumberOnly, setPhoneNumberOnly] = useState("");

  // ── Form State ──
  const [formData, setFormData] = useState({
    recipients: [],
    broadcastType: "now", 
    scheduleDateTime: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  // Fetch Templates
  // Fetch Templates
  useEffect(() => {
    const fetchTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const headers = { "X-Tenant-ID": localStorage.getItem("tenant") || "" };
        const response = await axios.get(GET_RCS_TEMPLATES, { headers, params: { bot_id: bot?.id, limit: 500 } });
        const templatesData = response.data.results || response.data || [];
        const approvedTemplates = templatesData.filter((t) => t.status === "APPROVED");
        setTemplates(approvedTemplates);
      } catch (error) {
        console.error("Failed to fetch templates", error);
      } finally {
        setTemplatesLoading(false);
      }
    };
    
    // 🟢 FIX: Always fetch the templates for the grid!
    if (bot?.id) {
      fetchTemplates();
    }
  }, [bot?.id]);

  // Fetch Segments
  useEffect(() => {
    if (recipientType === "segment") {
      const fetchSegments = async () => {
        setSegmentsLoading(true);
        try {
          const token = await getValidToken();
          const response = await axios.get(AUDIENCE_SEGMENT_LIST_ENDPOINT, {
            headers: { "X-Tenant-ID": localStorage.getItem("tenant") || "", Authorization: `Bearer ${token}` },
          });
          if (response.data && response.data.results) setSegments(response.data.results);
        } catch (error) {
          console.error("Failed to fetch segments:", error);
        } finally {
          setSegmentsLoading(false);
        }
      };
      fetchSegments();
    }
  }, [recipientType]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => t.name.toLowerCase().includes(templateSearch.toLowerCase()));
  }, [templates, templateSearch]);

  const isValidPhone = (phone) => {
    try {
      // Ensure the '+' is present for the library to detect the country correctly
      const formattedPhone = phone.startsWith('+') ? phone : `+${phone}`;
      return isValidPhoneNumber(formattedPhone);
    } catch (e) {
      return false;
    }
  };

  // ===== CSV UPLOAD METHODS =====
  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Optional: Basic file type check before sending to backend
    const validTypes = ["text/csv", "application/vnd.ms-excel"];
    if (!validTypes.includes(file.type) && !file.name.endsWith(".csv")) {
      setErrors({ recipients: "Please upload a valid CSV file." });
      setCsvFile(null);
      return;
    }

    // Only checks the file has records; the backend validates the rows themselves
    if (!csvHasDataRows(await file.text())) {
      setErrors({ recipients: EMPTY_CSV_ERROR });
      setCsvFile(null);
      event.target.value = "";
      return;
    }

    setCsvFile(file);
    setErrors((prev) => ({ ...prev, recipients: null }));
  };

  const handleClearCsv = () => {
    setCsvFile(null);
    setErrors((prev) => ({ ...prev, recipients: null }));
    const fileInput = document.getElementById("csv-upload");
    if (fileInput) fileInput.value = "";
  };

  // ===== SEGMENT METHODS =====
  const handleSegmentToggle = (segment) => {
    if (selectedSegments[0]?.id === segment.id) {
      setSelectedSegments([]);
    } else {
      setSelectedSegments([segment]);
      setErrors((prev) => ({ ...prev, recipients: null }));
    }
  };

  // ===== SINGLE CONTACT METHODS =====
  // Single broadcast = exactly one recipient: the typed number (with the selected country) when it is valid
  const setSingleRecipient = (country, rawPhone) => {
    const fullNumber = `${country.dialCode}${rawPhone}`;
    setFormData((prev) => ({ ...prev, recipients: rawPhone && isValidPhoneNumber(fullNumber) ? [fullNumber] : [] }));
    setErrors((prev) => ({ ...prev, recipients: null }));
  };

  const handleClearRecipients = () => {
    setFormData((prev) => ({ ...prev, recipients: [] }));
    setCsvFile(null);
    setValidationErrors([]);
    setRecipientInput("");
    setPhoneNumberOnly("");
    setSelectedSegments([]);
  };

  const handleRecipientTypeChange = (type) => {
    handleClearRecipients();
    setRecipientType(type);
    setErrors({});
  };

  // ===== STEPPER LOGIC =====
  const handleNext = () => {
    if (activeStep === 0 && !selectedTemplate) {
      setErrors({ template: "Please select a template to continue" });
      return;
    }
    
    if (activeStep === 1) {
      if (recipientType === "csv" && !csvFile) {
      setErrors({ recipients: "Please upload a valid CSV file" });
      return;
    }
      if (recipientType === "segment" && selectedSegments.length === 0) {
        setErrors({ recipients: "Please select an audience segment" });
        return;
      }
      if (recipientType === "single" && formData.recipients.length === 0) {
        setErrors({ recipients: "Please enter a valid phone number" });
        return;
      }
    }

    setErrors({});
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const getTotalSegmentCount = () => selectedSegments.reduce((total, segment) => total + segment.count, 0);

  const calculateEstimatedCost = () => {
    let totalRecipients = recipientType === "segment" ? getTotalSegmentCount() : formData.recipients.length;
    return totalRecipients * 1.5; 
  };

  // ===== SUBMIT HANDLER =====
  const handleSubmit = async () => {
    if (formData.broadcastType === "scheduled" && !formData.scheduleDateTime) {
      setErrors({ scheduleDateTime: "Please select a date and time for the scheduled broadcast" });
      return;
    }

    setLoading(true);
    setBroadcastResult(null);

    try {
      const token = await getValidToken();
      if (!token) throw new Error("Authentication failed. Please login again.");
      
      const headers = { "X-Tenant-ID": localStorage.getItem("tenant") || "", Authorization: `Bearer ${token}` };

      let finalCampaignName = campaignName.trim();
      if (recipientType === "single") finalCampaignName = "RCS_Single Contact";
      // Campaigns (Beta) campaigns can hold WhatsApp and RCS broadcasts, so their names aren't channel-prefixed
      else if (!fromCampaignsV2) finalCampaignName = `RCS_${finalCampaignName.replace(/^RCS_/i, "")}`;
      
      const finalCampaignDesc = campaignDescription.trim() || `RCS Broadcast via ${recipientType}`;

      let payload;

      if (recipientType === "csv") {
        payload = new FormData();
        payload.append("template_id", selectedTemplate.id);
        payload.append("bot_id", bot.id);
        payload.append("recipient_type", "csv");
        payload.append("file", csvFile); 
        payload.append("campaign_name", finalCampaignName);
        payload.append("campaign_description", finalCampaignDesc);
        payload.append("schedule_type", formData.broadcastType === "scheduled" ? "scheduled" : "immediate"); 
        Object.entries(campaignsV2Fields).forEach(([key, value]) => value && payload.append(key, value));
        if (formData.broadcastType === "scheduled") payload.append("schedule_datetime", formData.scheduleDateTime);
        delete headers["Content-Type"];
      } else {
        headers["Content-Type"] = "application/json";
        payload = {
          template_id: selectedTemplate.id,
          bot_id: bot.id,
          recipient_type: recipientType,
          schedule_type: formData.broadcastType === "scheduled" ? "scheduled" : "immediate", 
          schedule_datetime: formData.broadcastType === "scheduled" ? formData.scheduleDateTime : null,
          campaign_name: finalCampaignName,
          campaign_description: finalCampaignDesc,
        };
        Object.entries(campaignsV2Fields).forEach(([key, value]) => {
          if (value) payload[key] = value;
        });

        if (recipientType === "segment") payload.filter_id = selectedSegments[0]?.id;
        else payload.phone_numbers = formData.recipients;
      }

      const response = await axios.post(RCS_BROADCAST_TEMPLATE, payload, { headers });

      if (response.status === 200 || response.status === 201) {
        // 1. Trigger the success toast
        setSuccessAlert("Broadcast has been created successfully");
        setCreated(true);
        singlePage?.onSubmitted?.();
        
        // 2. 🟢 FIX: Directly navigate and pass the activeTab param via the router
        setTimeout(() => {
          if (fromCampaignsV2) {
            props.onNavigate("campaigns_v2", { tab: campaignsV2Params.returnTab });
          } else if (props?.onNavigate) {
            props.onNavigate("marketing_broadcast", { activeTab: 2 }); 
          } else {
            onBack(); 
          }
        }, 2000);
      }
    } catch (error) {
      let msg = error.response?.data?.details || error.response?.data?.error || error.message || "Failed to launch broadcast";
      if (typeof msg === "object") msg = JSON.stringify(msg, null, 2);
      setBroadcastResult({ success: false, message: "Broadcast Failed", error: msg });
      if (onError) onError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Audience choice (CSV / segment / single): type cards + the chosen type's inputs, shared by the wizard and the single page
  const audienceTypeCards = (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
          <Paper
          sx={{
              ...(recipientType === "csv" ? selectedCardStyle : cardStyle),
              pointerEvents: initialCampaignType === "quick" ? "none" : "all",
              opacity: initialCampaignType === "quick" ? 0.6 : 1,
          }}
          onClick={() => initialCampaignType !== "quick" && handleRecipientTypeChange("csv")}
          >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
              <CloudUploadIcon sx={{ width: 24, height: 24, color: "#4B5563" }} />
              <Radio value="csv" checked={recipientType === "csv"} sx={{ color: "#D1D5DB", "&.Mui-checked": { color: ColorsConstants.primaryBlue100 }, p: 0.5 }} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: "#505050", mb: "6px" }}>Upload via CSV</Typography>
              <Typography sx={{ ...typography.bodySmall, color: "#6B7280" }}>Upload Audience using CSV</Typography>
          </Box>
          </Paper>
      </Grid>

      <Grid item xs={12} sm={4}>
          <Paper
          sx={{
              ...(recipientType === "segment" ? selectedCardStyle : cardStyle),
              pointerEvents: initialCampaignType === "quick" ? "none" : "all",
              opacity: initialCampaignType === "quick" ? 0.6 : 1,
          }}
          onClick={() => initialCampaignType !== "quick" && handleRecipientTypeChange("segment")}
          >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
              <GroupIcon sx={{ width: 24, height: 24, color: "#4B5563" }} />
              <Radio value="segment" checked={recipientType === "segment"} sx={{ color: "#D1D5DB", "&.Mui-checked": { color: ColorsConstants.primaryBlue100 }, p: 0.5 }} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: "#505050", mb: "6px" }}>Send using Segments</Typography>
              <Typography sx={{ ...typography.bodySmall, color: "#6B7280" }}>Choose from predefined audience segments</Typography>
          </Box>
          </Paper>
      </Grid>

      <Grid item xs={12} sm={4}>
          <Paper
          sx={{
              ...(recipientType === "single" ? selectedCardStyle : cardStyle),
              pointerEvents: initialCampaignType === "standard" ? "none" : "all",
              opacity: initialCampaignType === "standard" ? 0.6 : 1,
          }}
          onClick={() => initialCampaignType !== "standard" && handleRecipientTypeChange("single")}
          >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
              <PersonIcon sx={{ width: 24, height: 24, color: "#4B5563" }} />
              <Radio value="single" checked={recipientType === "single"} sx={{ color: "#D1D5DB", "&.Mui-checked": { color: ColorsConstants.primaryBlue100 }, p: 0.5 }} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: "#505050", mb: "6px" }}>Single Contact</Typography>
              <Typography sx={{ ...typography.bodySmall, color: "#6B7280" }}>Send to one specific Phone number</Typography>
          </Box>
          </Paper>
      </Grid>
    </Grid>
  );
  const audienceInputs = (
    <>

    <Divider sx={{ width: "100%", mt: 2, mb: 3 }} />

    {recipientType === "csv" && (
      <Box>
        <input accept=".csv,.xlsx,.xls" style={{ display: "none" }} id="csv-upload" type="file" onChange={handleCsvUpload} />
        <label htmlFor="csv-upload">
          <UploadArea sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "160px", p: 3 }}>
            <CloudUploadIcon sx={{ fontSize: 48, color: ColorsConstants.primaryBlue100, mb: 2 }} />
            <Typography variant="h6" gutterBottom>Upload CSV File</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>CSV with phone numbers (phone_no column required)</Typography>
            <ButtonOne variant="outlined" component="span" startIcon={<UploadFileIcon />} sx={{ borderColor: ColorsConstants.primaryBlue100, color: ColorsConstants.primaryBlue100 }}>
              Browse Files
            </ButtonOne>
          </UploadArea>
        </label>
        
        {csvFile && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>File:</strong> {csvFile.name}
            </Typography>
          </Alert>
        )}
        
        {errors.recipients && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.recipients}
          </Alert>
        )}
        
        <Typography variant="body2" sx={{ mt: 1 }}>
          Click{" "}
          <a
            href="/sample.csv"
            download="sample.csv"
            style={{ color: ColorsConstants.primaryBlue100, textDecoration: "none", fontWeight: "bold" }}
          >
            here
          </a>{" "}
          to download sample file.
        </Typography>
      </Box>
    )}

    {recipientType === "segment" && (
      <Box>
        {singlePage ? (
          // Campaigns (Beta): the same segment dropdown as the WhatsApp form
          <Box sx={{ mt: 1 }}>
            <AppDropdown
              menuItems={segments.map((segment) => ({ label: segment.name, value: segment.id, raw: segment }))}
              selectedValue={selectedSegments[0]?.id || ""}
              onMenuSelect={(value, item) => {
                setSelectedSegments([item.raw]);
                setErrors((prev) => ({ ...prev, recipients: null }));
              }}
              variant="outlined"
              endIcon={<ExpandMoreIcon />}
              paperProps={{ maxHeight: "200px", overflowY: "auto" }}
              sx={{ width: "100%" }}
            >
              <span style={{ ...typography.bodyLarge }}>
                {segmentsLoading ? "Loading segments..." : selectedSegments[0]?.name || "Select a Segment *"}
              </span>
            </AppDropdown>
          </Box>
        ) : (
        <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select a segment to broadcast to:</Typography>
        {segmentsLoading ? <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} /> : (
          <List sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, maxHeight: 300, overflow: "auto" }}>
            {segments.length > 0 ? segments.map((segment) => {
                const isSelected = selectedSegments.some((s) => s.id === segment.id);
                return (
                  <ListItem key={segment.id} button onClick={() => handleSegmentToggle(segment)} sx={{ bgcolor: isSelected ? `${ColorsConstants.primaryBlue100}10` : "transparent" }}>
                    <ListItemIcon><Radio checked={isSelected} sx={{ color: ColorsConstants.primaryBlue100 }} disableRipple /></ListItemIcon>
                    <ListItemText primary={<Typography variant="subtitle2" fontWeight={500}>{segment.name}</Typography>} secondary={`Created by: ${segment.created_by_name || "System"} • ${new Date(segment.last_updated).toLocaleDateString()}`} />
                  </ListItem>
                );
              }) : <ListItem><ListItemText primary="No segments found" /></ListItem>}
          </List>
        )}
        {selectedSegments.length > 0 && <Alert severity="info" sx={{ mt: 2 }}>Selected: <strong>{selectedSegments[0].name}</strong></Alert>}
        </>
        )}
      </Box>
    )}

    {recipientType === "single" && (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a country and enter the recipient's phone number
        </Typography>
        
        <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'flex-start' }}>
          
          {/* Country Dropdown */}
          <Autocomplete
            options={countryOptions}
            filterOptions={filterCountryCodeOptions}
            getOptionLabel={(option) => `${option.label} (${option.dialCode})`}
            value={selectedCountry}
            onChange={(event, newValue) => {
              if (!newValue) return;
              setSelectedCountry(newValue);
              setSingleRecipient(newValue, phoneNumberOnly);
            }}
            disableClearable
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Typography sx={typography.bodySmall}>{option.label}</Typography>
                <Typography sx={{ ...typography.bodySmall, color: 'text.secondary' }}>{option.dialCode}</Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField 
                {...params} 
                size="small" 
                placeholder="Search country..."
                sx={{ 
                  width: 220,
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" }
                }} 
              />
            )}
          />

          {/* Phone Number Input */}
          <Box sx={{ flexGrow: 1 }}>
            <TextField 
              fullWidth 
              size="small" 
              placeholder="Enter phone number" 
              value={phoneNumberOnly} 
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, ''); // Only allow digits
                setPhoneNumberOnly(digits);
                setSingleRecipient(selectedCountry, digits);
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment>
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" } }}
            />
          </Box>
        </Stack>
      </Box>
    )}
    {errors.recipients && <Alert severity="error" sx={{ mt: 2 }}>{errors.recipients}</Alert>}
    </>
  );
  const audienceSection = (
    <>
      {audienceTypeCards}
      {audienceInputs}
    </>
  );

  // Campaigns (Beta) single page: run the wizard's checks, then the normal submit
  const handleSinglePageSubmit = () => {
    if (!singlePage.validateSetup()) return;
    if (!selectedTemplate) return setErrors({ template: "Please select a template" });
    if (recipientType === "csv" && !csvFile) return setErrors({ recipients: "Please upload a valid CSV file" });
    if (recipientType === "segment" && selectedSegments.length === 0) return setErrors({ recipients: "Please select an audience segment" });
    if (recipientType === "single" && formData.recipients.length === 0) return setErrors({ recipients: "Please enter a valid phone number" });
    const fallbackProblem = fallbackAvailable ? fallbackError(fallback) : null;
    if (fallbackProblem) return setErrors({ fallback: fallbackProblem });
    setErrors({});
    handleSubmit();
  };

  if (broadcastResult) {
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", mt: 4 }}>
        <ResultCard success={broadcastResult.success} sx={{ borderRadius: "12px" }}>
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              {broadcastResult.success ? <CheckCircleIcon sx={{ fontSize: 48, color: "success.main" }} /> : <ErrorIcon sx={{ fontSize: 48, color: "error.main" }} />}
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{broadcastResult.message}</Typography>
                {broadcastResult.batchId && <Typography variant="body2" color="text.secondary">Batch ID: {broadcastResult.batchId}</Typography>}
              </Box>
            </Stack>
            {broadcastResult.data && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Details:</Typography>
                <Typography variant="body2" component="pre" sx={{ fontSize: "0.75rem", whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(broadcastResult.data.message, null, 2)}
                </Typography>
              </Alert>
            )}
            {broadcastResult.error && <Alert severity="error" sx={{ mb: 2 }}><Typography variant="body2">{broadcastResult.error}</Typography></Alert>}
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end", p: 3, pt: 0 }}>
            <Button variant="outlined" onClick={exitTo}>Return to Campaigns</Button>
            {broadcastResult.success && <Button variant="contained" onClick={() => window.location.reload()} sx={{ bgcolor: ColorsConstants.primaryBlue100 }}>Create New</Button>}
          </CardActions>
        </ResultCard>
      </Box>
    );
  }


  if (singlePage) {
    const scheduledAt = formData.scheduleDateTime ? new Date(formData.scheduleDateTime) : null;
    const audienceSummary =
      recipientType === "csv"
        ? csvFile?.name
        : recipientType === "segment" && selectedSegments.length
          ? `${selectedSegments.map((segment) => segment.name).join(", ")}${getTotalSegmentCount() ? ` · ${getTotalSegmentCount()} contacts` : ""}`
          : recipientType === "single" && formData.recipients.length
            ? formData.recipients.join(", ")
            : null;
    return (
      <Box>
        {(errors.template || errors.scheduleDateTime || errors.fallback) && (
          <Alert severity="error" sx={{ mb: 2 }}>{errors.template || errors.scheduleDateTime || errors.fallback}</Alert>
        )}
        <BroadcastSections
          channel="rcs"
          templateOptions={templates}
          getTemplateLabel={(template) => template.name}
          selectedTemplate={selectedTemplate}
          onTemplateSelect={(template) => {
            setSelectedTemplate(template);
            setErrors({});
          }}
          templateMeta={selectedTemplate ? [
            { label: "Type", value: RCS_TEMPLATE_TYPE_LABELS[selectedTemplate.template_type] || selectedTemplate.template_type },
            { label: "Category", value: selectedTemplate.template_use_case },
          ] : []}
          templateNote="Only approved templates of the selected bot are listed."
          templateFormRef={null}
          templateFields={null}
          preview={<RCSTemplatePreview template={selectedTemplate} bot={bot} />}
          audience={
            singlePage.quick ? audienceInputs : (
              <AudienceTypePicker value={recipientType} onChange={handleRecipientTypeChange} csv={audienceInputs} segment={audienceInputs} />
            )
          }
          audienceTitle={singlePage.quick ? "Recipient" : "Audience"}
          hideSchedule={!!singlePage.quick}
          audienceSummary={audienceSummary}
          isLater={formData.broadcastType === "scheduled"}
          onSendNow={() => setFormData((prev) => ({ ...prev, broadcastType: "now", scheduleDateTime: "" }))}
          onSendLater={() => setFormData((prev) => ({ ...prev, broadcastType: "scheduled" }))}
          scheduleDate={scheduledAt}
          scheduleTime={scheduledAt}
          onScheduleApply={(date, time) => {
            const when = new Date(date);
            const at = new Date(time);
            when.setHours(at.getHours(), at.getMinutes(), 0, 0);
            setFormData((prev) => ({ ...prev, scheduleDateTime: format(when, "yyyy-MM-dd'T'HH:mm") }));
          }}
          showRetry={false}
          fallback={fallbackAvailable ? fallback : undefined}
          setFallback={setFallback}
          submitLabel={singlePage.submitLabel}
          onCancel={exitTo}
          onSubmit={handleSinglePageSubmit}
          loading={loading || created}
        />
        {successAlert && (
          <Alert severity="success" onClose={() => setSuccessAlert("")} sx={{ mt: 2, borderRadius: "8px" }}>
            {successAlert}
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8, pt: 2, px: { xs: 2, md: 4 } }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <BackButton onClick={exitTo} />
        <Box>
          <Typography sx={{ ...typography.h1, color: ColorsConstants.primaryBlue100 }}>
            {campaignName ? `Launch "${campaignName}"` : "Launch Broadcast"}
          </Typography>
          <Typography variant="body2" color="text.secondary">Configure and schedule your campaign</Typography>
        </Box>
      </Stack>

      <Box sx={{ mb: 4, width: "100%", maxWidth: 800, mx: "auto" }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          
          {/* STEP 1: Select Template */}
          {activeStep === 0 && (
            <Paper elevation={0} sx={{ border: "1px solid #DFE5EC", p: 3, borderRadius: "12px", minHeight: 500 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography sx={{ ...typography.h3, color: ColorsConstants.primaryBlue100 }}>
                  Select an Approved Template
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search templates..."
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  sx={{ width: 250, "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" } }}
                />
              </Stack>

              {errors.template && <Alert severity="error" sx={{ mb: 3 }}>{errors.template}</Alert>}

              {templatesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
              ) : filteredTemplates.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8, color: "#6B7280" }}>
                   <MessageIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
                   <Typography>No approved templates found matching your search.</Typography>
                </Box>
              ) : (
                <Box sx={{ height: 600, overflowY: "auto", pr: 1, "&::-webkit-scrollbar": { width: 6 }, "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.15)", borderRadius: 3 } }}>
                  <Grid container spacing={2}>
                    {filteredTemplates.map((t) => {
                      const isSelected = selectedTemplate?.id === t.id;
                      return (
                        <Grid item xs={12} sm={6} md={6} key={t.id}>
                          <RCSTemplateGridCard 
                            template={t} 
                            isSelected={isSelected} 
                            onClick={() => { setSelectedTemplate(t); setErrors((prev) => ({ ...prev, template: null })); }} 
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}
            </Paper>
          )}

          {/* STEP 2: Select Campaign Type */}
          {activeStep === 1 && (
            <Paper elevation={0} sx={{ border: "1px solid #DFE5EC", p: 3, borderRadius: "12px", minHeight: 500 }}>
              <Typography sx={{ ...typography.h3, mb: 3, color: ColorsConstants.primaryBlue100 }}>Select Campaign Type:</Typography>
              {audienceSection}
            </Paper>
          )}

          {/* STEP 3: Broadcast Type (Now vs Schedule) */}
          {activeStep === 2 && (
            <Paper elevation={0} sx={{ border: "1px solid #DFE5EC", p: 3, borderRadius: "12px", minHeight: 500 }}>
              <Typography sx={{ ...typography.h3, mb: 3, color: ColorsConstants.primaryBlue100 }}>Broadcast Type</Typography>

              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6}>
                    <Paper
                    sx={{ ...(formData.broadcastType === "now" ? selectedCardStyle : cardStyle) }}
                    onClick={() => setFormData(prev => ({...prev, broadcastType: "now"}))}
                    >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FlashOnIcon sx={{ color: formData.broadcastType === 'now' ? ColorsConstants.primaryBlue100 : "#4B5563" }} />
                            <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>Now</Typography>
                        </Box>
                        <Radio value="now" checked={formData.broadcastType === "now"} sx={{ p: 0, color: "#D1D5DB", "&.Mui-checked": { color: ColorsConstants.primaryBlue100 } }} />
                    </Box>
                    <Typography sx={{ ...typography.bodySmall, color: "#6B7280", mt: 1 }}>Deliver immediately to all selected recipients</Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Paper
                    sx={{ 
                      ...cardStyle, 
                      pointerEvents: "none", 
                      opacity: 0.5 // This greys out the entire card
                    }}
                    >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarTodayIcon sx={{ color: "#4B5563" }} />
                            <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>Schedule</Typography>
                        </Box>
                        {/* Explicitly disable the radio button */}
                        <Radio disabled value="scheduled" checked={false} sx={{ p: 0, color: "#D1D5DB" }} />
                    </Box>
                    <Typography sx={{ ...typography.bodySmall, color: "#6B7280", mt: 1 }}>Choose a specific date and time for delivery</Typography>
                    </Paper>
                </Grid>
              </Grid>

              {formData.broadcastType === "scheduled" && (
                <Box sx={{ mb: 4, p: 2, border: "1px solid #E5E7EB", borderRadius: "8px", backgroundColor: "#F9FAFB" }}>
                  <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 2 }}>Schedule Date & Time</Typography>
                  <TextField fullWidth type="datetime-local" name="scheduleDateTime" value={formData.scheduleDateTime || ""} onChange={handleChange} error={!!errors.scheduleDateTime} helperText={errors.scheduleDateTime} InputLabelProps={{ shrink: true }} inputProps={{ min: new Date().toISOString().slice(0, 16) }} sx={{ maxWidth: 400, backgroundColor: "#fff" }} />
                </Box>
              )}

              <Typography sx={{ ...typography.h3, mb: 2, color: ColorsConstants.primaryBlue100 }}>Final Summary</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ borderRadius: "8px", bgcolor: "#f8f9fa" }}>
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                        {recipientType === "segment" ? getTotalSegmentCount() : formData.recipients.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Recipients</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ borderRadius: "8px", bgcolor: "#f8f9fa" }}>
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <Typography variant="h5" sx={{ color: "#388e3c", fontWeight: 700 }}>{calculateEstimatedCost().toFixed(2)}</Typography>
                      <Typography variant="body2" color="text.secondary">Est. Credits</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Stepper Navigation Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, pt: 2, borderTop: "1px solid #E5E7EB" }}>
            <ButtonTwo variant="outlined" onClick={activeStep === 0 ? exitTo : handleBack} sx={{ borderColor: "#D1D5DB", minWidth: 85, color: "#6B7280", "&:hover": { borderColor: "#9CA3AF" } }}>
              {activeStep === 0 ? "Cancel" : "Back"}
            </ButtonTwo>
            
            {activeStep < 2 ? (
              <ButtonOne variant="contained" onClick={handleNext} sx={{ backgroundColor: ColorsConstants.primaryBlue100 }}>Next</ButtonOne>
            ) : (
              <ButtonOne variant="contained" onClick={handleSubmit} disabled={loading || created} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />} sx={{ backgroundColor: ColorsConstants.primaryBlue100 }}>
                {loading ? "Processing..." : formData.broadcastType === 'scheduled' ? "Schedule Broadcast" : "Launch Broadcast"}
              </ButtonOne>
            )}
          </Box>
          {/* 🟢 NEW: Inline Success Alert matching WhatsApp UI */}
          {successAlert && (
            <Alert 
              severity="success" 
              onClose={() => setSuccessAlert("")}
              sx={{ mt: 3, borderRadius: "8px", alignItems: "center" }}
            >
              {successAlert}
            </Alert>
          )}
        </Grid>

        {/* Right Column - Sticky Template Preview */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ position: "sticky", top: 20 }}>
            <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: "12px", bgcolor: "#F9FAFB" }}>
              <Typography sx={{ ...typography.h3, mb: 2, color: ColorsConstants.primaryBlue100, textAlign: "center" }}>Live Preview</Typography>
              <Divider sx={{ mb: 3 }} />
              <RCSTemplatePreview template={selectedTemplate} bot={bot} />
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BroadcastTemplate;